import { useEffect, useState } from "react";
import { deleteApoderado, listApoderados, saveApoderado } from "../../lib/data";
import { formatRut, maskTelefono, normalizeEmail, normalizeRut, isValidRut } from "../../lib/format";
import type { Apoderado } from "../../lib/types";
import { EmptyState, Modal, Spinner } from "../../components/ui";

const BLANK: Apoderado = {
  id: "", nombre: "", rut: "", email: "", telefono: "", estado: "activo", creadoEn: "",
};

export function Apoderados() {
  const [items, setItems] = useState<Apoderado[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Apoderado>(BLANK);
  const [error, setError] = useState("");

  async function reload() {
    setLoading(true);
    try {
      setItems(await listApoderados());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  function nuevo() { setDraft(BLANK); setError(""); setOpen(true); }
  function editar(a: Apoderado) { setDraft(a); setError(""); setOpen(true); }

  async function guardar() {
    if (!draft.nombre.trim()) return setError("El nombre es obligatorio");
    if (!isValidRut(draft.rut)) return setError("RUT inválido");
    if (!normalizeEmail(draft.email)) return setError("Correo obligatorio");
    try {
      await saveApoderado({
        ...draft,
        rut: normalizeRut(draft.rut),
        email: normalizeEmail(draft.email),
      });
      setOpen(false);
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function eliminar(a: Apoderado) {
    if (!confirm(`¿Eliminar a ${a.nombre}?`)) return;
    await deleteApoderado(a.id);
    reload();
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">Apoderados</h1>
          <p className="text-sm text-slate-500">Contactos responsables de los alumnos</p>
        </div>
        <button className="btn-primary" onClick={nuevo}>+ Nuevo</button>
      </div>

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState title="Sin apoderados" hint="Crea el primero con el botón + Nuevo." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="th">Nombre</th><th className="th">RUT</th>
                <th className="th">Correo</th><th className="th">Teléfono</th>
                <th className="th">Estado</th><th className="th"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {items.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="td font-medium">{a.nombre}</td>
                  <td className="td">{formatRut(a.rut)}</td>
                  <td className="td">{a.email}</td>
                  <td className="td">{a.telefono || "—"}</td>
                  <td className="td">
                    <span className={`badge ${a.estado === "activo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {a.estado}
                    </span>
                  </td>
                  <td className="td text-right">
                    <button className="btn-ghost mr-1 !px-2 !py-1" onClick={() => editar(a)}>Editar</button>
                    <button className="btn-danger !px-2 !py-1" onClick={() => eliminar(a)}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={draft.id ? "Editar apoderado" : "Nuevo apoderado"}>
        <div className="space-y-3">
          <div>
            <label className="label">Nombre completo</label>
            <input className="input" value={draft.nombre} onChange={(e) => setDraft({ ...draft, nombre: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">RUT</label>
              <input className="input" value={draft.rut} placeholder="12.345.678-9"
                onChange={(e) => setDraft({ ...draft, rut: formatRut(e.target.value) })} />
            </div>
            <div>
              <label className="label">Teléfono</label>
              <input className="input" value={draft.telefono} placeholder="+56 9 XXXX XXXX"
                onChange={(e) => setDraft({ ...draft, telefono: maskTelefono(e.target.value) })} />
            </div>
          </div>
          <div>
            <label className="label">Correo (usado para el login del apoderado)</label>
            <input className="input" type="email" value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </div>
          <div>
            <label className="label">Estado</label>
            <select className="input" value={draft.estado}
              onChange={(e) => setDraft({ ...draft, estado: e.target.value as Apoderado["estado"] })}>
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
            <button className="btn-primary" onClick={guardar}>Guardar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
